"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Cpu, Sparkles, Trophy } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png-wvc-srcset",
  sizes: "(min-width: 1024px) 112px, (min-width: 640px) 96px, 64px",
  alt: "ROBOTO Academy Mascot"
};
const CONTENT = {
  vi: {
    badge: "Học viện Ươm mầm Robotics & STEM",
    heading: "Xây dựng bản lĩnh sáng tạo, Coding và tự tin thi đấu đỉnh cao.",
    subheading: "Trải nghiệm phòng lab công nghệ chuẩn quốc tế với trang thiết bị Engineering chuyên dụng. Học viên phát triển tư duy thuật toán thực chiến cùng đội ngũ cố vấn giải đấu quốc gia.",
    metrics: [{
      tag: "Quy chuẩn lớp học",
      title: "Tỷ lệ 1:6 Tận tâm",
      desc: "Kèm cặp sát sao, theo sát từng bước tiến của học viên"
    }, {
      tag: "Lộ trình đào tạo",
      title: "Độ tuổi 4 đến 16",
      desc: "Từ nền tảng LEGO, Robotics đến lập trình Python nâng cao"
    }, {
      tag: "Đấu trường công nghệ",
      title: "Chuẩn mực quốc tế",
      desc: "Bệ phóng tài năng cho các giải đấu Robotics danh giá"
    }],
    cta: "Đăng ký học thử miễn phí",
    microcopy: "Không yêu cầu nền tảng Coding trước đó. Toàn bộ thiết bị thí nghiệm được cung cấp tại phòng lab."
  },
  en: {
    badge: "Robotics & STEM Incubator",
    heading: "Build the confidence to create, code, and compete.",
    subheading: "Step into our lab for a hands-on session with industry-grade hardware. Your child learns real computational thinking alongside national competition mentors.",
    metrics: [{
      tag: "Lab Ratio",
      title: "1:6 Mentor Focus",
      desc: "Individual coaching for every builder"
    }, {
      tag: "Curriculum",
      title: "Ages 4 to 16",
      desc: "From basic mechanisms to Python"
    }, {
      tag: "Tournaments",
      title: "Global Standards",
      desc: "Prepares students for prestigious cups"
    }],
    cta: "Start Your Free Trial",
    microcopy: "No prior coding experience required. All lab equipment provided on site."
  }
};
export default function Champions() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const savedLang = localStorage.getItem("roboto_lang");
      if (savedLang === "vi" || savedLang === "en") {
        setLang(savedLang);
      }
    } catch {
      // Ignore localStorage access issues
    }
    const handleCustomLangChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "roboto_lang" && (event.newValue === "vi" || event.newValue === "en")) {
        setLang(event.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomLangChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomLangChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const t = CONTENT[lang];
  const containerAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="join-our-next-champions" data-nav="light" className="relative bg-background py-20 md:py-24 text-foreground overflow-hidden">
      {/* Decorative Lab Blueprint Coordinate Pattern */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-border)_1px,transparent_1px)] bg-[size:32px_32px] opacity-40" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16 2xl:px-24">
        <motion.div variants={containerAnimation} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="relative mx-auto max-w-4xl rounded-2xl border border-border bg-card p-8 md:p-14 lg:p-16 shadow-xl text-center overflow-hidden">
          {/* Precision Spec Rail at top of card */}
          <div aria-hidden="true" className="absolute top-0 inset-x-0 h-1 bg-secondary z-20">
            <div className="h-1.5 w-3 bg-secondary -mt-0.5 rounded-sm" />
          </div>

          {/* Static Floating Decorative Mascot */}
          <div aria-hidden="true" className="pointer-events-none select-none absolute bottom-3 right-3 sm:bottom-5 sm:right-6 lg:bottom-7 lg:right-8 z-0">
            <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt={DEFAULT_MASCOT.alt} loading="lazy" className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
          </div>

          {/* Eyebrow badge */}
          <div className="relative z-10 inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/10 px-3.5 py-1 text-xs font-mono uppercase tracking-wider text-secondary mb-6">
            <Trophy className="size-3.5" />
            <span>{t.badge}</span>
          </div>

          {/* Heading */}
          <h2 className="relative z-10 font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-card-foreground mb-4">
            {t.heading}
          </h2>

          {/* Supporting line & body */}
          <p className="relative z-10 font-sans text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
            {t.subheading}
          </p>

          {/* Metric highlights */}
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 text-left">
            <div data-index={0} className="rounded-lg border border-border bg-muted/40 p-4 transition-[border-color] duration-200 hover:border-secondary/50">
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="flex size-7 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                  <Cpu className="size-4" />
                </div>
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wide">
                  {t.metrics[0].tag}
                </span>
              </div>
              <p className="font-serif text-lg font-semibold text-foreground">
                {t.metrics[0].title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t.metrics[0].desc}
              </p>
            </div>

            <div data-index={1} className="rounded-lg border border-border bg-muted/40 p-4 transition-[border-color] duration-200 hover:border-secondary/50">
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="flex size-7 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                  <Sparkles className="size-4" />
                </div>
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wide">
                  {t.metrics[1].tag}
                </span>
              </div>
              <p className="font-serif text-lg font-semibold text-foreground">
                {t.metrics[1].title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t.metrics[1].desc}
              </p>
            </div>

            <div data-index={2} className="rounded-lg border border-border bg-muted/40 p-4 transition-[border-color] duration-200 hover:border-secondary/50">
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="flex size-7 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                  <Trophy className="size-4" />
                </div>
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wide">
                  {t.metrics[2].tag}
                </span>
              </div>
              <p className="font-serif text-lg font-semibold text-foreground">
                {t.metrics[2].title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t.metrics[2].desc}
              </p>
            </div>
          </div>

          {/* Action trigger */}
          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-serif font-semibold tracking-normal px-8 py-6 text-base">
              <Link to="/FreeTrialContactPage" className="inline-flex items-center gap-2">
                <span>{t.cta}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          {/* Reassuring micro-copy */}
          <p className="relative z-10 text-xs text-muted-foreground mt-4 font-sans max-w-xs sm:max-w-md mx-auto">
            {t.microcopy}
          </p>
        </motion.div>
      </div>
    </section>;
}